  /**
   * Weekly Site Inspection Component
   * ===============================
   * 
   * Individual form component 
   * 
   * Base Components:
   * - EXTENDS BaseFormComponent<WeeklySiteInspection> for form logic inheritance
   * - USES FormLayoutComponent for step navigation UI (in template)
   * 
   * Form Configuration:
   * - Form structure, steps, sections, and field organization are defined in the form model
   * - Signature and upload requirements are configured via form model metadata
   * - Component implements abstract methods: initializeForm() and getFormData()
   * - Component overrides setupDefaultStepValidation() for custom step validation rules
   * 
   * Key Functionality:
   * - Creates nested form groups for logical organization (basic, hazards, signOff)
   * - Implements dynamic hazard management with add/remove functionality (up to 4 hazards)
   * - Provides comprehensive site inspection with hazard identification and control methods
   * - Implements form validation for safety compliance and inspection requirements
   * - Implements step validation with signature requirements for final step
   * - Flattens nested form structure for backend submission
   * 
   * Unique Complex Logic:
   * - Dynamic hazard field management with add/remove functionality (up to 4 hazards)
   * - Boolean array tracking for dynamic UI rendering of hazard fields
   * - Real-time form structure modification with hazard field visibility management
   * - Conditional validation setup for hazard fields that become required when added
   * 
   * Form Model vs Component Responsibilities:
   * - FORM MODEL: Defines step/section structure, field mappings, attachment requirements, and form metadata
   * - COMPONENT: Handles Angular form validation, form group creation, conditional field logic, and overrides base form methods
   * 
   * @author MacSafety Development Team
   * @version 1.0
   */

import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { BaseFormComponent } from '../../base-form.component';
import { FormStep } from '../../../../../models/forms/form-steps.interface';
import { WeeklySiteInspection } from '../../../../../models/forms/weekly-site-inspection/weekly-site-inspection.model';
import { FormService } from '../../../../../services/form.service';
import { EntraService } from '../../../../../services/entra.service';
import { AttachmentUploadService } from '../../../../../services/attachment-upload.service';
import { SignatureService } from '../../../../../services/signature.service';
import { ToastService } from '../../../../../services/toast.service';
import { ScreenSizeService } from '../../../../../services/screen-size.service';
import { FormModelService } from '../../../../../services/form-model.service';

@Component({
  selector: 'app-weekly-site-inspection',
  templateUrl: './weekly-site-inspection.component.html',
  styleUrls: ['./weekly-site-inspection.component.css'],
})
export class WeeklySiteInspectionComponent
  extends BaseFormComponent<WeeklySiteInspection>
  implements OnInit
{
  override formType = WeeklySiteInspection.getFormInfo().formType;
  override steps: FormStep[] = WeeklySiteInspection.getFormSteps();
  override model = new WeeklySiteInspection();

  userName: string = '';

  hazardCount: number = 1;
  showAddHazardButton: boolean = true;
  showHazardIndex: Array<boolean> = [true, false, false, false];

  // Form group getters
  get basicFormGroup(): FormGroup {
    return this.form.get('basic') as FormGroup;
  }

  get hazardsFormGroup(): FormGroup {
    return this.form.get('hazards') as FormGroup;
  }



  constructor(
    protected override fb: FormBuilder,
    protected override formService: FormService,
    protected override router: Router,
    protected override entraService: EntraService,
    protected override attachmentService: AttachmentUploadService,
    protected override toastService: ToastService,
    protected override signatureService: SignatureService,
    protected override screenSizeService: ScreenSizeService,
    protected formModelService: FormModelService
  ) {
    super(
      fb,
      formService,
      router,
      entraService,
      attachmentService,
      toastService,
      signatureService,
      screenSizeService
    );
  

    this.formMetadata = this.formModelService.getFormMetadata(this.formType)!;
    this.userName = this.entraService.userName();
  }

  

  override ngOnInit(): void {
    super.ngOnInit();
  }

  protected override initializeForm(): void {
    this.form = this.fb.group({
      basic: this.fb.group({
        date: [this.model.date, Validators.required],
        jobName: [this.model.jobName, Validators.required],
        inspector: [this.userName, Validators.required],
      }),
      hazards: this.fb.group({
        hazardObserved1: [this.model.hazardObserved1, Validators.required],
        location1: [this.model.location1, Validators.required],
        correctiveAction1: [this.model.correctiveAction1, Validators.required],
        hazardObserved2: [this.model.hazardObserved2],
        location2: [this.model.location2],
        correctiveAction2: [this.model.correctiveAction2],
        hazardObserved3: [this.model.hazardObserved3],
        location3: [this.model.location3],
        correctiveAction3: [this.model.correctiveAction3],
        hazardObserved4: [this.model.hazardObserved4],
        location4: [this.model.location4],
        correctiveAction4: [this.model.correctiveAction4],
      }),

    });
  }

  protected override getFormData(): WeeklySiteInspection {
    const formValue = this.form.value;
    // Flatten the nested form groups into a single object
    const flattenedData = {
      ...formValue.basic,
      ...formValue.hazards,
   
    };   
    const weeklySiteInspection = new WeeklySiteInspection(flattenedData);
    const updatedWeeklySiteInspection = this.updateModelWithAttachments(weeklySiteInspection);
    return updatedWeeklySiteInspection;
  }

  /**
   * Sets up validation rules for each step of the weekly site inspection form.
   * Each step has its own validation requirements and must be visited to be considered valid:
   * - Basic: Validates date, job name, and inspector fields, requires step to be visited
   * - Hazards: Validates hazard observation, location, and corrective action fields, requires step to be visited
   * - Attachments: Checks if required attachments are present and if the step has been visited
   */
   protected override setupDefaultStepValidation(): void {
    // Skip validation setup if noFormRequirements is true
    if (!this.shouldApplyFormRequirements()) {
      return;
    }
    // Define step indices
    const PHOTO_REQUIREMENT_INDEX: number = 0;

    // Custom validation rules for each step using the form groups directly
    this.setupStepValidation('basic', this.basicFormGroup);
    this.setupStepValidation('hazards', this.hazardsFormGroup);

  }

  addHazard() {
    if (this.hazardCount < 4) {
      this.hazardCount++;
      const hazardCount = this.hazardCount++;
      this.showHazardIndex[hazardCount - 1] = true;
      this.hazardCount = hazardCount;
      if (hazardCount === 4) {
        this.showAddHazardButton = false;
      }
    }
  }
}
